import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import TextField from "../common/TextField";
import Button from "../common/Button";
import SingleSelect from "../common/SingleSelect";
import ConfirmDialoag from "../common/ConfirmDialoag";
import {
  FUEL_TYPE_OPTIONS,
  FALLBACK_VEHICLE_TYPES,
  formatVinDetailValue,
  getVinDetailEntries,
  getVinDetailLabel,
  mapVehicleToQuoteFields,
  normalizeVinFuelType,
  resolveCityOption,
  resolveVinCategory,
  toMakeOption,
} from "./quoteUtils";
import {
  createQuoteVehicle,
  decodeVin,
  getMakesPage,
  getVehicleModels,
  getVehicleTypeNames,
  searchVehicleByVin,
} from "./quotesApi";

const Q = {
  line: "#e2e9ef",
  navy: "#1a1d4f",
  muted: "#6b7280",
};

const EMPTY_VEHICLE_FIELDS = {
  vehicleId: null,
  plate: "",
  make: "",
  model: "",
  modelId: "",
  year: "",
  fuelType: "",
  vehicleType: "",
  cityId: null,
  label: "",
  parentId: null,
};

const normalizeCityPlateRegex = (regexStr) => {
  if (!regexStr) return "";
  return String(regexStr).replace(/(^|[^\\])s([*+])/g, (_, before, quant) => {
    return before + "\\s" + quant;
  });
};

const isValidLicensePlate = (plate, city, cities = []) => {
  if (!plate || !String(plate).trim()) return false;
  const plateValue = String(plate).trim();
  const arabicPlate =
    /^[\u0621-\u064A]\s[\u0621-\u064A]\s[\u0621-\u064A]\s[\d٠-٩]{4}$/;
  if (arabicPlate.test(plateValue)) return true;

  const cityId = city && typeof city === "object" ? city.id ?? city.value : city;
  const selectedCity =
    (cities || []).find((c) => String(c.id) === String(cityId)) ||
    (city && typeof city === "object" ? city : null);

  if (!selectedCity?.regex) return false;
  try {
    const pattern = normalizeCityPlateRegex(selectedCity.regex);
    return new RegExp(pattern).test(plateValue);
  } catch (e) {
    return false;
  }
};

const withSelectError = (hasError, styles = {}) => ({
  ...styles,
  control: (base, state) => {
    const extra =
      typeof styles.control === "function"
        ? styles.control(base, state)
        : { ...base, ...(styles.control || {}) };
    return {
      ...extra,
      borderColor: hasError ? "#ff424c" : extra.borderColor,
      "&:hover": {
        ...(extra["&:hover"] || {}),
        borderColor: hasError ? "#ff424c" : extra.borderColor,
      },
    };
  },
});

const SectionCard = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: "#fff",
      border: "1px solid",
      borderColor: Q.line,
      borderRadius: "12px",
      p: "16px 18px 8px",
      mb: 1,
      overflow: "visible",
    }}
  >
    {title ? (
      <Typography sx={{ mb: 1.5, fontSize: 14, fontWeight: 700, color: Q.navy }}>
        {title}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const VehicleDetailsSection = ({
  form,
  setForm,
  fieldErrors = {},
  clearFieldError,
  disabled,
  translate,
  logInUser,
}) => {
  const cities = useSelector((state) => state.Cities?.cities) || [];
  const [vehicleTypes, setVehicleTypes] = useState(FALLBACK_VEHICLE_TYPES);
  const [allMakes, setAllMakes] = useState([]);
  const [selectedMakeTypes, setSelectedMakeTypes] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [yearError, setYearError] = useState(false);
  const [vinError, setVinError] = useState("");
  const [plateError, setPlateError] = useState(false);
  const [pendingVin, setPendingVin] = useState(null);
  const [vehicleSearching, setVehicleSearching] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [vehicleNotFound, setVehicleNotFound] = useState(false);
  const lastSearchedVin = useRef("");
  const makesPagination = useRef({
    page: 1,
    search: "",
    hasNext: false,
    loading: false,
  });

  const plateValid = isValidLicensePlate(form.plate, form.cityId, cities);
  const cityOptions = useMemo(
    () =>
      (cities || []).map((city) => ({
        ...city,
        id: city.id,
        label: city.cityEn || city.name || city.label || String(city.id),
        name: city.cityEn || city.name || city.label || String(city.id),
      })),
    [cities],
  );
  const selectedCityValue = useMemo(
    () => resolveCityOption(form.cityId, cities),
    [form.cityId, cities],
  );
  const selectedCityMsg = selectedCityValue?.msg || "";

  useEffect(() => {
    // Edit mode: vehicle fields are read-only — skip category/makes/models APIs
    if (disabled) {
      if (form.vin) {
        const vin = String(form.vin || "")
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .slice(0, 17);
        setVinError(
          vin && vin.length !== 17
            ? translate(
                "VIN must be exactly 17 characters long and contain only alphanumeric characters.",
              )
            : "",
        );
      }
      if (form.plate) {
        setPlateError(!isValidLicensePlate(form.plate, form.cityId, cities));
      }
      return;
    }

    let cancelled = false;
    fetchVehicleTypes();
    fetchMakes("");
    const makeLabel =
      form.make?.label || (typeof form.make === "string" ? form.make : "");
    if (makeLabel) {
      fetchVehicleModels(makeLabel).then((models) => {
        if (cancelled) return;
        setSelectedMakeTypes(models);
        if (form.model && !form.modelId?.id) {
          const found = models.find(
            (item) =>
              (item.modelName || item.label || "").toLowerCase() ===
              String(form.model).toLowerCase(),
          );
          if (found) {
            setForm((prev) => ({
              ...prev,
              modelId: {
                id: found.id,
                label: found.modelName,
                modelName: found.modelName,
              },
            }));
          }
        }
      });
    }
    if (form.vin) {
      const vin = String(form.vin || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 17);
      setVinError(
        vin && vin.length !== 17
          ? translate(
              "VIN must be exactly 17 characters long and contain only alphanumeric characters.",
            )
          : "",
      );
      if (vin.length === 17) lastSearchedVin.current = vin;
    }
    if (form.year) {
      const parsedYear = parseInt(form.year, 10);
      const currentYear = new Date().getFullYear();
      setYearError(
        Number.isNaN(parsedYear) ||
          parsedYear < 1950 ||
          parsedYear > currentYear + 1,
      );
    }
    if (form.plate) {
      setPlateError(!isValidLicensePlate(form.plate, form.cityId, cities));
    }
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  // Keep form.cityId enriched with cityEn/regex when cities load (edit)
  useEffect(() => {
    if (!cities?.length || !form.cityId) return;
    const resolved = resolveCityOption(form.cityId, cities);
    if (!resolved?.cityEn && !resolved?.name && !resolved?.regex) return;
    const sameId =
      String(form.cityId?.id ?? form.cityId) === String(resolved.id);
    const alreadyNamed = Boolean(form.cityId?.cityEn || form.cityId?.name);
    if (sameId && alreadyNamed) return;
    setForm((prev) => ({ ...prev, cityId: resolved }));
  }, [cities, form.cityId?.id ?? form.cityId]);

  const fetchVehicleTypes = async () => {
    const names = await getVehicleTypeNames();
    setVehicleTypes(names);
  };

  const fetchMakes = async (search = "") => {
    try {
      setMakesLoading(true);
      const { options, hasNext } = await getMakesPage(1, search);
      makesPagination.current = { page: 1, search, hasNext, loading: false };
      setAllMakes(options);
    } catch (error) {
      setAllMakes([]);
    } finally {
      setMakesLoading(false);
    }
  };

  const searchMakesList = (search = "") => {
    if (makesPagination.current.timer) {
      clearTimeout(makesPagination.current.timer);
    }
    makesPagination.current.timer = setTimeout(() => {
      fetchMakes(search);
    }, 300);
  };

  const loadMoreMakes = async () => {
    const pag = makesPagination.current || {};
    if (!pag.hasNext || pag.loading) return;
    makesPagination.current.loading = true;
    setMakesLoading(true);
    try {
      const nextPage = pag.page + 1;
      const { options, hasNext } = await getMakesPage(nextPage, pag.search);
      setAllMakes((prev) => [...(prev || []), ...options]);
      makesPagination.current = {
        page: nextPage,
        search: pag.search,
        hasNext,
        loading: false,
      };
    } catch (error) {
      makesPagination.current.loading = false;
    } finally {
      setMakesLoading(false);
    }
  };

  const fetchVehicleModels = async (selectedMake) => {
    if (!selectedMake) {
      setSelectedMakeTypes([]);
      setModelsLoading(false);
      return [];
    }
    setModelsLoading(true);
    try {
      const rows = await getVehicleModels(selectedMake);
      setSelectedMakeTypes(rows);
      return rows;
    } finally {
      setModelsLoading(false);
    }
  };

  const handleChange = (name, value) => {
    if (name === "make") {
      if (!value) {
        setSelectedMakeTypes([]);
        setForm((prev) => ({ ...prev, make: "", modelId: "", model: "" }));
        return;
      }
      const makeOption = toMakeOption(value);
      setForm((prev) => ({
        ...prev,
        make: makeOption,
        modelId: "",
        model: "",
      }));
      fetchVehicleModels(makeOption.label);
      return;
    }
    if (name === "modelId") {
      setForm((prev) => ({
        ...prev,
        modelId: value || "",
        model: value?.modelName || value?.label || "",
      }));
      return;
    }
    if (name === "fuel_type") {
      if (value && clearFieldError) clearFieldError("fuelType");
      setForm((prev) => ({
        ...prev,
        fuelType: value?.id || value?.key || value || "",
      }));
      return;
    }
    if (name === "cityId") {
      if (value && clearFieldError) clearFieldError("cityId");
      setForm((prev) => {
        const next = { ...prev, cityId: value || null };
        setPlateError(
          Boolean(next.plate) && !isValidLicensePlate(next.plate, value, cities),
        );
        return next;
      });
      return;
    }
    if (name === "plate") {
      const plate = value || "";
      const valid = isValidLicensePlate(plate, form.cityId, cities);
      setPlateError(Boolean(plate) && !valid);
      if (plate && valid && clearFieldError) clearFieldError("plate");
      setForm((prev) => ({ ...prev, plate }));
      return;
    }
    if (name === "year") {
      const year = value;
      const parsedYear = parseInt(year, 10);
      const currentYear = new Date().getFullYear();
      setYearError(
        Boolean(year) &&
          (Number.isNaN(parsedYear) ||
            parsedYear < 1950 ||
            parsedYear > currentYear + 1),
      );
      if (year && clearFieldError) clearFieldError("year");
      setForm((prev) => ({ ...prev, year }));
      return;
    }
    if (name === "vin") {
      const vin = String(value || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 17);
      setVinError(
        vin && vin.length !== 17
          ? translate(
              "VIN must be exactly 17 characters long and contain only alphanumeric characters.",
            )
          : "",
      );
      if (vin && vin.length === 17 && clearFieldError) clearFieldError("vin");
      setVehicleNotFound(false);
      if (vin !== lastSearchedVin.current) {
        lastSearchedVin.current = "";
      }
      setForm((prev) => ({
        ...prev,
        vin,
        vehicleId: vin === prev.vin ? prev.vehicleId : null,
      }));
      return;
    }
    if (value && clearFieldError) clearFieldError(name);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const clearVehicleDetails = () => {
    setSelectedMakeTypes([]);
    setYearError(false);
    setPlateError(false);
    setForm((prev) => ({
      ...prev,
      ...EMPTY_VEHICLE_FIELDS,
      vin: prev.vin,
    }));
  };

  const applyVehicleToForm = async (vehicle) => {
    const mapped = mapVehicleToQuoteFields(vehicle, cities);
    if (mapped.make) {
      setAllMakes((prev) => {
        const exists = (prev || []).some(
          (item) =>
            String(item.label || item.key || "").toLowerCase() ===
            String(mapped.make.label || "").toLowerCase(),
        );
        return exists ? prev : [mapped.make, ...(prev || [])];
      });
    }
    if (mapped.vehicleType) {
      setVehicleTypes((prev) => {
        const exists = (prev || []).some(
          (item) =>
            String(item).toLowerCase() ===
            String(mapped.vehicleType).toLowerCase(),
        );
        return exists ? prev : [...(prev || []), mapped.vehicleType];
      });
    }
    if (mapped.make?.label) {
      await fetchVehicleModels(mapped.make.label);
    }
    if (mapped.year) {
      const parsedYear = parseInt(mapped.year, 10);
      const currentYear = new Date().getFullYear();
      setYearError(
        Number.isNaN(parsedYear) ||
          parsedYear < 1950 ||
          parsedYear > currentYear + 1,
      );
    }
    setPlateError(
      Boolean(mapped.plate) &&
        !isValidLicensePlate(mapped.plate, mapped.cityId, cities),
    );
    if (clearFieldError) {
      clearFieldError(
        "vin",
        "plate",
        "vehicleType",
        "year",
        "fuelType",
        "vehicleId",
        "cityId",
      );
    }
    setForm((prev) => ({
      ...prev,
      ...mapped,
    }));
  };

  const searchVehicle = async (vinOverride) => {
    const vin = String(vinOverride || form.vin || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (!vin || vin.length !== 17) {
      return;
    }
    if (vehicleSearching) return;
    if (lastSearchedVin.current === vin && form.vehicleId) return;

    setVehicleSearching(true);
    setVehicleNotFound(false);
    lastSearchedVin.current = vin;
    try {
      const vehicle = await searchVehicleByVin(vin,logInUser);
      if (!vehicle) {
        setVehicleNotFound(true);
        clearVehicleDetails();
        toast.info(translate("vehicleNotFoundCreate"));
        return;
      }
      setVehicleNotFound(false);
      await applyVehicleToForm(vehicle);
      toast.success(translate("vehicleFound"));
    } catch (error) {
      setVehicleNotFound(true);
      clearVehicleDetails();
      toast.error(translate("somethingWentWrong"));
    } finally {
      setVehicleSearching(false);
    }
  };

  const handleVinBlur = () => {
    if (disabled) return;
    if ((form.vin || "").length === 17 && !vinError) {
      searchVehicle(form.vin);
    }
  };

  const canCreateVehicle = Boolean(
    !form.vehicleId &&
      form.vin &&
      form.vin.length === 17 &&
      !vinError &&
      form.plate &&
      form.cityId &&
      plateValid &&
      !plateError &&
      form.vehicleType &&
      form.make &&
      form.modelId &&
      form.year &&
      !yearError &&
      form.fuelType,
  );

  const persistVehicle = async () => {
    if (form.vehicleId) return;
    if (!canCreateVehicle) {
      toast.error(translate("pleaseFillRequiredFields"));
      return;
    }
    setVehicleSaving(true);
    try {
      const saved = await createQuoteVehicle(form, {
        createdBy: logInUser?.id || 0,
      });
      const vehicleId =
        saved?.id || saved?.data?.id || saved?.vehicle?.id || null;
      if (!vehicleId) {
        toast.error(translate("somethingWentWrong"));
        return;
      }
      setVehicleNotFound(false);
      setPlateError(false);
      setForm((prev) => ({ ...prev, vehicleId }));
      if (clearFieldError) clearFieldError("vehicleId", "plate", "cityId");
      toast.success(translate("vehicleAdded"));
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        (typeof error === "string" ? error : "");
      toast.error(message || translate("somethingWentWrong"));
    } finally {
      setVehicleSaving(false);
    }
  };

  const fetchVin = async () => {
    if (!form.vin || form.vin.length !== 17) {
      toast.error(
        translate(
          "VIN must be exactly 17 characters long and contain only alphanumeric characters.",
        ),
      );
      return;
    }
    try {
      const decoded = await decodeVin(form.vin);
      if (!decoded) {
        toast.error(translate("noRecordFound"));
        return;
      }
      setPendingVin(decoded);
    } catch (error) {
      toast.error(translate("somethingWentWrong"));
    }
  };

  const applyVin = async () => {
    const data = pendingVin || {};
    const maker = data.maker || data.manufacturerName;
    const modelName = data.model || data.modelName;
    const makeOption = toMakeOption(maker);
    const resolvedFuelType = normalizeVinFuelType(data.fuelType);
    const resolvedCategory = resolveVinCategory(data.bodyType, vehicleTypes);

    if (makeOption) {
      setAllMakes((prev) => {
        const exists = (prev || []).some(
          (item) =>
            String(item.label || item.key || "").toLowerCase() ===
            makeOption.label.toLowerCase(),
        );
        return exists ? prev : [makeOption, ...(prev || [])];
      });
    }
    if (resolvedCategory) {
      setVehicleTypes((prev) => {
        const exists = (prev || []).some(
          (item) =>
            String(item).toLowerCase() === resolvedCategory.toLowerCase(),
        );
        return exists ? prev : [...(prev || []), resolvedCategory];
      });
    }

    let matchedModel = "";
    if (maker) {
      const models = await fetchVehicleModels(maker);
      const found = (models || []).find(
        (item) =>
          (item?.modelName || item?.label || "").toLowerCase() ===
          String(modelName || "").toLowerCase(),
      );
      matchedModel = found
        ? {
            id: found.id,
            label: found.modelName,
            modelName: found.modelName,
            value: found.id,
          }
        : data.modelId
          ? { id: data.modelId, label: modelName || data.modelName, modelName }
          : modelName
            ? { id: modelName, label: modelName, modelName }
            : "";
    }

    setForm((prev) => ({
      ...prev,
      make: makeOption || prev.make,
      model: modelName || prev.model,
      modelId: matchedModel || prev.modelId,
      year: data.modelYear || prev.year,
      fuelType: resolvedFuelType || prev.fuelType,
      vehicleType: resolvedCategory || prev.vehicleType,
    }));
    if (data.modelYear) {
      const parsedYear = parseInt(data.modelYear, 10);
      const currentYear = new Date().getFullYear();
      setYearError(
        Number.isNaN(parsedYear) ||
          parsedYear < 1950 ||
          parsedYear > currentYear + 1,
      );
    }
    setPendingVin(null);
  };

  const vinDetailEntries = useMemo(
    () => (pendingVin ? getVinDetailEntries(pendingVin) : []),
    [pendingVin],
  );
  const vinPreview = pendingVin
    ? [pendingVin.maker, pendingVin.model, pendingVin.modelYear]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <>
      <SectionCard title={translate("vehicleDetails")}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label={translate("vin")}
              disabled={disabled}
              value={form.vin || ""}
              error={Boolean(vinError) || Boolean(fieldErrors.vin)}
              onChange={(e) => handleChange("vin", e.target.value)}
              onBlur={handleVinBlur}
              fullWidth
              inputProps={{ maxLength: 17 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          (form.vin || "").length === 17 && !vinError
                            ? "#548e25"
                            : "#999",
                      }}
                    >
                      {vehicleSearching
                        ? "..."
                        : `${(form.vin || "").length}/17`}
                    </span>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText error={Boolean(vinError)}>
              {vinError ||
                translate(
                  "VIN must be exactly 17 characters long and contain only alphanumeric characters.",
                )}
            </FormHelperText>
            {form.vehicleId ? (
              <FormHelperText sx={{ color: "#548e25" }}>
                {translate("vehicleId")}: {form.vehicleId}
              </FormHelperText>
            ) : vehicleNotFound ? (
              <FormHelperText error>
                {translate("vehicleNotFoundCreate")}
              </FormHelperText>
            ) : fieldErrors.vehicleId ? (
              <FormHelperText error>
                {translate("pleaseCreateOrSearchVehicle")}
              </FormHelperText>
            ) : null}
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                style={{ height: 28 }}
                disabled={disabled || (form.vin || "").length !== 17}
                onClick={fetchVin}
              >
                {translate("fetchData")}
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3} style={{ position: "relative", zIndex: 7 }}>
            <SingleSelect
              key={`city-${selectedCityValue?.id || "empty"}`}
              array={cityOptions}
              async
              isClearable
              selectName="cityId"
              label={translate("city")}
              isDisabled={disabled}
              value={
                selectedCityValue
                  ? {
                      id: selectedCityValue.id,
                      value: selectedCityValue.id,
                      label:
                        selectedCityValue.cityEn ||
                        selectedCityValue.name ||
                        selectedCityValue.label ||
                        String(selectedCityValue.id),
                    }
                  : ""
              }
              handleChange={handleChange}
              canRemove
              canAssign
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={withSelectError(fieldErrors.cityId, {
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={
                translate("vehicleLicensePlate") || translate("plateNumber")
              }
              disabled={disabled || !form.cityId}
              value={form.plate || ""}
              error={
                !form.vehicleId &&
                (Boolean(fieldErrors.plate) ||
                  (Boolean(form.plate) &&
                    Boolean(form.cityId) &&
                    !plateValid))
              }
              onChange={(e) => handleChange("plate", e.target.value)}
              fullWidth
            />
            {form.vehicleId ? null : form.cityId && form.plate && !plateValid ? (
              <FormHelperText error>
                {selectedCityMsg || translate("pleaseFillRequiredFields")}
              </FormHelperText>
            ) : selectedCityMsg && form.cityId ? (
              <FormHelperText>{selectedCityMsg}</FormHelperText>
            ) : fieldErrors.plate && !form.plate ? (
              <FormHelperText error>
                {translate("pleaseFillRequiredFields")}
              </FormHelperText>
            ) : null}
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label={translate("vehicleType")}
              disabled={disabled}
              value={form.vehicleType || ""}
              error={Boolean(fieldErrors.vehicleType)}
              onChange={(e) => handleChange("vehicleType", e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>{translate("none")}</em>
              </MenuItem>
              {vehicleTypes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
              {form.vehicleType &&
                !vehicleTypes.includes(form.vehicleType) && (
                  <MenuItem value={form.vehicleType}>
                    {form.vehicleType}
                  </MenuItem>
                )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3} style={{ position: "relative", zIndex: 6 }}>
            <SingleSelect
              array={allMakes || []}
              serverPaginated
              isClearable
              isLoading={makesLoading}
              id="make"
              selectName="make"
              label={translate("make")}
              isDisabled={disabled}
              onSearchChange={searchMakesList}
              onMenuScrollToBottom={loadMoreMakes}
              value={
                form.make
                  ? {
                      id: form.make.key || form.make.id,
                      value: form.make.label || form.make.value,
                      label: form.make.label || form.make.key,
                    }
                  : ""
              }
              handleChange={handleChange}
              canRemove
              canAssign
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Grid>
          <Grid item xs={12} md={3} style={{ position: "relative", zIndex: 6 }}>
            <SingleSelect
              key={form.make?.label || form.make?.id || "model"}
              array={selectedMakeTypes || []}
              async
              isClearable
              id="modelId"
              selectName="modelId"
              label={translate("model")}
              isDisabled={disabled || !form.make}
              isLoading={modelsLoading}
              value={
                form.modelId && form.modelId.id
                  ? {
                      id: form.modelId.id,
                      value: form.modelId.id,
                      label: form.modelId.label || form.modelId.modelName,
                    }
                  : ""
              }
              handleChange={handleChange}
              canRemove
              canAssign
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={translate("year")}
              type="number"
              disabled={disabled}
              value={form.year || ""}
              error={yearError || Boolean(fieldErrors.year)}
              onChange={(e) => handleChange("year", e.target.value)}
              fullWidth
            />
            <FormHelperText error={yearError}>
              {translate("Make year must be 1950 to current year ")}
            </FormHelperText>
          </Grid>
          <Grid item xs={12} md={3} style={{ position: "relative" }}>
            <label
              style={{
                fontSize: "0.75em",
                lineHeight: "1",
                position: "absolute",
                zIndex: 10,
                background: "#fff",
                padding: "0 5px",
                left: 14,
                color: fieldErrors.fuelType ? "#ff424c" : undefined,
              }}
            >
              {translate("fuel_type")}
            </label>
            <SingleSelect
              array={FUEL_TYPE_OPTIONS}
              id="fuel_type"
              async
              isClearable
              selectName="fuel_type"
              label={translate("fuel_type")}
              isDisabled={disabled}
              value={
                form.fuelType
                  ? {
                      id: form.fuelType,
                      label: form.fuelType,
                      value: form.fuelType,
                    }
                  : ""
              }
              handleChange={handleChange}
              canRemove
              canAssign
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={withSelectError(fieldErrors.fuelType, {
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 0.5, mb: 1 }}
            >
              <Typography sx={{ fontSize: 12, color: Q.muted }}>
                {form.vehicleId
                  ? translate("vehicleLinked")
                  : translate("vehicleRequiredForQuote")}
              </Typography>
              {!form.vehicleId ? (
                <Button
                  style={{ height: 28 }}
                  disabled={disabled || vehicleSaving || !canCreateVehicle}
                  onClick={persistVehicle}
                >
                  {vehicleSaving
                    ? translate("saving")
                    : translate("createVehicle")}
                </Button>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </SectionCard>

      {pendingVin && (
        <ConfirmDialoag
          title="vinDecoderDataFound"
          CancelText="no"
          OkText="yes"
          maxWidth="sm"
          onCancel={() => setPendingVin(null)}
          onOk={applyVin}
        >
          <p style={{ margin: 0 }}>
            {translate("vinDecoderDataFoundMessage")}
          </p>
          {vinPreview && (
            <p style={{ margin: "8px 0 0", fontWeight: 600 }}>{vinPreview}</p>
          )}
          {vinDetailEntries.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 320, overflowY: "auto" }}>
              {vinDetailEntries.map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "6px 0",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#333" }}>
                    {getVinDetailLabel(key, translate)}
                  </span>
                  <span
                    style={{
                      color: "#555",
                      marginLeft: 12,
                      textAlign: "right",
                    }}
                  >
                    {formatVinDetailValue(value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </ConfirmDialoag>
      )}
    </>
  );
};

export default VehicleDetailsSection;
